/**
 * <subscription-price>
 *
 * Keeps the headline price block in sync with the Recharge "Subscription Widget V2"
 * selection. When the widget is on a subscription option, the block shows the
 * subscription price with the one-time price struck through; on one-time it shows
 * the normal one-time price.
 *
 * Why this is needed: the theme's price block always renders the product's one-time
 * price, and the Recharge widget manages its own pricing separately. The existing
 * subscription-selector.js only reads Recharge on submit (pull model), so nothing
 * updates the headline price live. This element bridges that gap for display only.
 *
 * Data: a per-variant price map (rendered by Liquid) lets it stay correct across
 * variant changes too; variant-less products (e.g. floss) simply use their one entry.
 * Detection: a MutationObserver on the widget + click/change listeners re-read the
 * widget's `purchaseOption` / `sellingPlanId` reactive props. Safe fallback: if the
 * widget never appears, the one-time price stays shown (identical to old behavior).
 */
if (!customElements.get('subscription-price')) {
  customElements.define(
    'subscription-price',
    class SubscriptionPrice extends HTMLElement {
      connectedCallback() {
        this.onetimeEl = this.querySelector('.subscription-price__onetime');
        this.subEl = this.querySelector('.subscription-price__sub');
        this.subPriceEl = this.querySelector('[data-sub-price]');
        this.onetimePriceEl = this.querySelector('[data-onetime-price]');
        this.productId = this.dataset.productId;

        try {
          this.data = JSON.parse(this.querySelector('.subscription-price-data')?.textContent || '{}');
        } catch (e) {
          this.data = {};
        }

        // Re-sync when the theme updates the one-time price (i.e. a variant change).
        if (this.onetimeEl) {
          this._priceObserver = new MutationObserver(() => this.refresh());
          this._priceObserver.observe(this.onetimeEl, { childList: true, subtree: true, characterData: true });
        }

        // Quick View has no Recharge widget: a custom <purchase-options> toggle drives the
        // price via this event. When set, its value overrides the widget read in sync().
        this._onPurchaseOption = (e) => {
          if (!this.productId || String(e.detail.productId) === String(this.productId)) {
            this._override = e.detail.isSubscription;
            this.sync();
          }
        };
        document.addEventListener('purchase-option:change', this._onPurchaseOption);

        this.bindWidget();
        this.refresh();
      }

      disconnectedCallback() {
        this._priceObserver?.disconnect();
        this._widgetObserver?.disconnect();
        clearInterval(this._pollTimer);
        clearInterval(this._syncTimer);
        document.removeEventListener('purchase-option:change', this._onPurchaseOption);
      }

      /** The variant id currently selected in the product form (or the first available). */
      currentVariantId() {
        const input =
          document.querySelector(`form[data-product-id="${this.productId}"] [name="id"]`) ||
          document.querySelector('form[is="product-form"] [name="id"]') ||
          document.querySelector('[name="id"]');
        return input ? String(input.value) : Object.keys(this.data)[0] || null;
      }

      /** The Recharge widget for THIS product (falls back to any on the page). */
      get widget() {
        return (
          document.querySelector(`recharge-subscription-widget[product-id="${this.productId}"]`) ||
          document.querySelector('recharge-subscription-widget')
        );
      }

      /** True unless the widget is explicitly on the one-time option. */
      isSubscription() {
        const w = this.widget;
        if (!w) return false;
        const option = w.purchaseOption;
        if (option === 'onetime') return false;
        if (typeof option === 'string' && option.length) return true;
        return Boolean(w.sellingPlanId);
      }

      /** The widget upgrades async; attach once it exists, then keep polling briefly as a backstop. */
      bindWidget() {
        const attach = () => {
          const w = this.widget;
          if (!w || this._bound) return false;
          this._bound = true;
          // Event/observer hooks for instant response...
          this._widgetObserver = new MutationObserver(() => this.sync());
          this._widgetObserver.observe(w, { attributes: true, childList: true, subtree: true });
          w.addEventListener('click', () => setTimeout(() => this.sync(), 0));
          w.addEventListener('change', () => setTimeout(() => this.sync(), 0));
          // ...plus a lightweight persistent poll as a bulletproof backstop: the
          // Recharge widget is an opaque third-party element that may update its
          // `purchaseOption` without a DOM mutation we can observe.
          clearInterval(this._syncTimer);
          this._syncTimer = setInterval(() => this.sync(), 400);
          this.sync();
          return true;
        };
        if (attach()) return;
        let tries = 0;
        this._pollTimer = setInterval(() => {
          if (attach() || tries++ > 40) clearInterval(this._pollTimer);
        }, 250);
      }

      /** Update the displayed prices for the current variant, then re-evaluate which price shows. */
      refresh() {
        const id = this.currentVariantId();
        const entry = id ? this.data[id] : null;
        this._variantHasSub = Boolean(entry && entry.sub);
        if (entry) {
          if (this.subPriceEl && entry.sub) this.subPriceEl.textContent = entry.sub;
          // "was" = higher of compare-at and one-time (see the Liquid); fall back for older markup.
          const was = 'was' in entry ? entry.was : entry.sub !== entry.onetime ? entry.onetime : null;
          if (this.onetimePriceEl && was) this.onetimePriceEl.textContent = was;
        }
        // Hide the struck-through one-time price when it matches the subscription
        // price (no subscription discount) — showing an identical crossed-out price
        // is misleading.
        if (this.onetimePriceEl) {
          const was = entry && ('was' in entry ? entry.was : entry.sub !== entry.onetime ? entry.onetime : null);
          const hasDiscount = Boolean(entry && entry.sub && was);
          this.onetimePriceEl.hidden = !hasDiscount;
        }
        this.sync();
      }

      /** Show the subscription price only when the widget is on a subscription AND this variant has one. */
      sync() {
        // Prefer the custom <purchase-options> state (Quick View, no widget): the retained
        // global covers the initial-load race, the event covers live changes; fall back to the
        // Recharge widget on the PDP.
        const st = (window.__purchaseOptionState || {})[this.productId];
        const isSub = st ? st.isSubscription : this._override != null ? this._override : this.isSubscription();
        if (this._variantHasSub && isSub) {
          this.showSub();
        } else {
          this.showOnetime();
        }
      }

      showSub() {
        if (this.onetimeEl) this.onetimeEl.hidden = true;
        if (this.subEl) this.subEl.hidden = false;
      }

      showOnetime() {
        if (this.onetimeEl) this.onetimeEl.hidden = false;
        if (this.subEl) this.subEl.hidden = true;
      }
    }
  );
}
